import {useState} from 'react';
import {
  Box,
  Button,
  Grow,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import {Close, NotificationsActive} from '@mui/icons-material';
import {useNotifications} from '@/hooks/useInitializeNotifications';
import {useClientType} from '@/hooks/useClientType/useClientType';

type DesktopNotificationPromptProps = {
  isPopover: boolean;
};

export const DesktopNotificationPrompt = ({
  isPopover,
}: DesktopNotificationPromptProps) => {
  const {palette} = useTheme();
  const [dismissed, setDismissed] = useState(false);
  const clientType = useClientType();
  const {status, subscribe} = useNotifications();

  const isVisible = clientType === 'ENT' && !dismissed && status === 'default';

  if (isPopover) {
    return (
      <Grow in={isVisible} timeout={500}>
        <Paper
          elevation={3}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 9999,
            p: 3,
            maxWidth: 320,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <NotificationsActive
                sx={{
                  color: palette.primary.faded,
                  fontSize: 24,
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: palette.text.primary,
                }}
              >
                Enable Notifications
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setDismissed(true)}
              data-attr="desktop-notification-prompt-dismiss"
              sx={{
                color: palette.text.secondary,
                mt: -0.5,
                mr: -0.5,
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: palette.text.secondary,
              lineHeight: 1.5,
            }}
          >
            Stay updated with real-time alerts about messages, applications, and
            important community updates.
          </Typography>
          <Button
            variant="contained"
            onClick={subscribe}
            data-attr="desktop-notification-prompt-enable"
            fullWidth
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              py: 1,
              backgroundColor: palette.primary.faded,
              '&:hover': {
                backgroundColor: palette.primary.faded,
                opacity: 0.9,
              },
            }}
          >
            Turn On Notifications
          </Button>
        </Paper>
      </Grow>
    );
  }

  return (
    <Grow in={isVisible} timeout={500}>
      <Box
        sx={{
          p: 2,
          backgroundColor:
            palette.mode === 'dark'
              ? 'rgba(21, 101, 159, 0.08)'
              : 'rgba(21, 101, 159, 0.04)',
          borderRadius: 2,
          border: `1px solid ${
            palette.mode === 'dark'
              ? 'rgba(21, 101, 159, 0.16)'
              : 'rgba(21, 101, 159, 0.12)'
          }`,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <NotificationsActive
            sx={{
              color: palette.primary.faded,
              fontSize: 20,
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: palette.text.primary,
              fontSize: 13,
            }}
          >
            Stay Updated
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: palette.text.secondary,
            fontSize: 12,
            lineHeight: 1.4,
          }}
        >
          Get real-time alerts for messages, applications, and important
          community updates.
        </Typography>
        <Button
          variant="contained"
          onClick={subscribe}
          data-attr="desktop-notification-prompt-enable"
          size="small"
          fullWidth
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 12,
            py: 0.75,
            backgroundColor: palette.primary.faded,
            '&:hover': {
              backgroundColor: palette.primary.faded,
              opacity: 0.9,
            },
          }}
        >
          Enable Notifications
        </Button>
      </Box>
    </Grow>
  );
};
